import { useMemo } from 'react';
import { useBuyContext } from './BuyContext';
import {
  cardHasOptions,
  resolve,
  type CardConfig,
  type ResolveCtx,
} from './cardSchema';

export type UseBuyTotalsReturn = {
  basePrice: number;
  addonsTotal: number;
  total: number;
};

const walkAddonsTotal = (
  cards: CardConfig[],
  selections: Record<string, string | null | string[]>,
  ctx: ResolveCtx,
): number => {
  let sum = 0;
  for (const card of cards) {
    if (card.kind === 'subsection') {
      sum += walkAddonsTotal([card.child], selections, ctx);
      continue;
    }
    if (card.kind === 'composite') {
      sum += walkAddonsTotal(card.children, selections, ctx);
      continue;
    }
    if (card.kind === 'actionCard') continue;
    if (!cardHasOptions(card)) continue;

    const selected = selections[card.id];
    if (selected == null) continue;
    const selectedIds = Array.isArray(selected) ? selected : [selected];

    for (const optionId of selectedIds) {
      const option = card.options.find((o) => o.id === optionId);
      if (!option) continue;
      const maybePrice = (option as { price?: unknown }).price;
      const resolved = resolve(
        maybePrice as Parameters<typeof resolve<number>>[0],
        ctx,
      );
      if (typeof resolved === 'number') sum += resolved;
    }
  }
  return sum;
};

export const useBuyTotals = (): UseBuyTotalsReturn => {
  const { cards, selections, basePrice, resolveCtx } = useBuyContext();

  const addonsTotal = useMemo(
    () => walkAddonsTotal(cards, selections, resolveCtx),
    [cards, selections, resolveCtx],
  );

  return { basePrice, addonsTotal, total: basePrice + addonsTotal };
};
